/**
 * UrqlMetricsExchange - Custom urql exchange for GraphQL performance tracking
 *
 * This exchange measures the duration of GraphQL operations and reports them
 * to Datadog RUM as custom duration vitals. This enables us to:
 * - Track query/mutation performance over time
 * - Identify slow operations
 * - Set up alerts on performance degradation
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/browser/tracking_user_actions/#custom-vitals
 */

import { isNullish } from '@breezy/shared'
import { datadogRum } from '@datadog/browser-rum'
import { OperationDefinitionNode } from 'graphql'
import { Exchange, Operation, OperationResult } from 'urql'
import { map, pipe, tap } from 'wonka'

/**
 * Tracks pending operation timing info.
 * Key is the urql operation key (unique per operation instance).
 */
type OperationTiming = {
  /** UNIX timestamp in milliseconds (Date.now()) for Datadog startTime parameter */
  startTime: number
  /** High-resolution timestamp (performance.now()) for precise duration calculation */
  startPerf: number
  operationName: string
  operationType: 'query' | 'mutation' | 'subscription'
}

// Store for tracking in-flight operations
const pendingOperations = new Map<number, OperationTiming>()

// Track subscriptions that have received their first event
// We only report "time to first event" once per subscription
const subscriptionsWithFirstEvent = new Set<number>()

// Max age for pending operations before cleanup (5 minutes)
const STALE_OPERATION_MS = 5 * 60 * 1000

/**
 * Clean up stale pending operations to prevent memory leaks.
 * Operations older than STALE_OPERATION_MS are removed.
 */
const cleanupStaleOperations = (): void => {
  const now = performance.now()
  for (const [key, timing] of pendingOperations.entries()) {
    if (now - timing.startPerf > STALE_OPERATION_MS) {
      pendingOperations.delete(key)
    }
  }
}

/**
 * Extract the operation name from a GraphQL operation.
 * Falls back to 'anonymous' if the operation has no name.
 */
const getOperationName = (operation: Operation): string => {
  const definition = operation.query.definitions[0] as OperationDefinitionNode
  return definition.name?.value ?? 'anonymous'
}

/**
 * Check if Datadog RUM has been initialized.
 * We need this check because RUM may not be initialized in all environments
 * (e.g., local development with rum.enabled=false).
 */
const isRumReady = (): boolean => !isNullish(datadogRum.getInitConfiguration())

/**
 * urql exchange that tracks GraphQL operation duration and reports to Datadog RUM.
 *
 * How it works:
 * 1. When an operation starts, we record the start time
 * 2. When the result comes back, we calculate duration
 * 3. We report the duration to Datadog as a custom vital
 *
 * Metrics reported:
 * - `graphql.query` - Duration of GraphQL queries
 * - `graphql.mutation` - Duration of GraphQL mutations
 * - `graphql.subscription.firstEvent` - Time from subscription start to first event
 *
 * For subscriptions, we only track "time to first event" since they're long-lived
 * connections where total duration isn't meaningful.
 *
 * Exchange position: Should be FIRST in the exchange chain to capture
 * the full operation lifecycle including retries and error handling.
 *
 * @example
 * ```typescript
 * const client = createClient({
 *   url: '/graphql',
 *   exchanges: [
 *     metricsExchange,  // First!
 *     mapExchange({...}),
 *     cacheExchange,
 *     fetchExchange,
 *   ],
 * })
 * ```
 */
export const metricsExchange: Exchange = ({ forward }) => {
  return ops$ => {
    return pipe(
      ops$,
      // Record start time for each operation
      tap((operation: Operation) => {
        // Clean up on teardown operations (when a query component unmounts)
        if (operation.kind === 'teardown') {
          pendingOperations.delete(operation.key)
          subscriptionsWithFirstEvent.delete(operation.key)
          return
        }

        // Clean up stale operations to prevent memory leaks
        cleanupStaleOperations()

        pendingOperations.set(operation.key, {
          startTime: Date.now(),
          startPerf: performance.now(),
          operationName: getOperationName(operation),
          operationType: operation.kind,
        })
      }),
      // Forward to next exchange
      forward,
      // Process results and report metrics
      map((result: OperationResult) => {
        const timing = pendingOperations.get(result.operation.key)

        // No timing info means this is a teardown result
        if (!timing) return result

        // Handle subscriptions: only report "time to first event"
        if (timing.operationType === 'subscription') {
          // Skip if we've already reported first event for this subscription
          if (subscriptionsWithFirstEvent.has(result.operation.key)) {
            return result
          }

          // Mark that we've received the first event
          subscriptionsWithFirstEvent.add(result.operation.key)

          // Calculate time to first event using high-resolution timer
          const duration = performance.now() - timing.startPerf

          // Report to Datadog if RUM is ready
          if (isRumReady()) {
            datadogRum.addDurationVital('graphql.subscription.firstEvent', {
              startTime: timing.startTime,
              duration,
              context: {
                operationName: timing.operationName,
                hasError: !!result.error,
                isNetworkError: !!result.error?.networkError,
                isGraphQLError: (result.error?.graphQLErrors?.length ?? 0) > 0,
              },
            })
          }

          return result
        }

        // For queries and mutations: report full duration and clean up
        pendingOperations.delete(result.operation.key)

        // Calculate duration in milliseconds using high-resolution timer
        const duration = performance.now() - timing.startPerf

        // Report to Datadog if RUM is ready
        if (isRumReady()) {
          // Use addDurationVital for proper duration tracking
          // This creates metrics that can be visualized in RUM Performance
          datadogRum.addDurationVital(`graphql.${timing.operationType}`, {
            startTime: timing.startTime,
            duration,
            context: {
              // These become facets in Datadog for filtering/grouping
              operationName: timing.operationName,
              operationType: timing.operationType,
              hasError: !!result.error,
              isNetworkError: !!result.error?.networkError,
              isGraphQLError: (result.error?.graphQLErrors?.length ?? 0) > 0,
            },
          })
        }

        return result
      }),
    )
  }
}
